import { isOnWeb, type AssetManifestEntry } from "@greg-chick/shared";

/**
 * 📂 **접히는 한 줄** — 목록이 길어질 때 「전부 펼쳐 놓기」를 대신한다.
 *
 * ## 왜 필요한가
 * [🖼️ 그림 전체 목록]의 캐릭터 칸은 한 명이 그림 자리를 일곱 개 쓴다. 여덟 명이면
 * **큰 카드가 쉰여섯 장**이다. 전부 펼쳐 두면 찾는 것이 어디 있는지 알 수가 없다.
 *
 * 접어 두면 한 줄에 **그림·이름·몇 장 채웠는지**가 보이고, 손볼 것만 펼치면 된다.
 *
 * ## 🔴 여는 단추 안에 다른 단추를 넣지 않는다
 * `<summary>` 안에 버튼을 넣으면 그 버튼을 눌러도 접힘이 같이 열린다(브라우저 기본 동작).
 * 그래서 [✕ 제거] 같은 것은 **펼친 안쪽**에 둔다 — 지우기를 열림/닫힘과 섞으면
 * 실수로 누르기도 쉽다.
 */
export function Fold({
  title,
  id,
  thumb,
  keys,
  assets,
  open,
  children,
}: {
  title: string;
  /** 괄호 안에 붙는 짧은 식별자(운영자가 배치할 때 쓰는 값). */
  id?: string;
  /** 접힌 줄에 보일 작은 그림. */
  thumb?: string;
  /** 이 줄이 거느린 그림 자리들 — 몇 개나 채웠는지 세어 보여 준다. */
  keys: readonly string[];
  assets: Record<string, AssetManifestEntry | undefined>;
  open?: boolean;
  children: React.ReactNode;
}) {
  const filled = keys.filter((k) => !!assets[k]?.url).length;
  // 🙈 「웹에서 내림」이 하나라도 있으면 말해 준다 — 그림은 채워져 있는데 아이 화면엔 없다.
  const off = keys.filter((k) => assets[k]?.url && !isOnWeb(assets[k])).length;
  return (
    <details className="fold" open={open}>
      <summary className="fold__sum">
        {thumb ? (
          <img className="fold__thumb" src={thumb} alt="" loading="lazy" />
        ) : (
          <span className="fold__thumb fold__thumb--empty" aria-hidden="true">
            🖼️
          </span>
        )}
        <span className="fold__title">
          {title}
          {id ? <em className="fold__id">({id})</em> : null}
        </span>
        {off > 0 ? <span className="fold__off">🙈 {off}</span> : null}
        <span className="fold__count" data-full={filled === keys.length || undefined}>
          {filled}/{keys.length}
        </span>
      </summary>
      <div className="fold__body">{children}</div>
    </details>
  );
}
